import { z } from 'zod'

export const MIN_HEIGHT_CM = 100
export const MIN_WEIGHT_KG = 10

interface CampaignRegisterSchemaOptions {
  // Form đăng ký bắt buộc giới tính/chiều cao/cân nặng; các form dùng chung
  // schema (sửa địa chỉ giao hàng, sửa hồ sơ) không có đủ ô này hoặc có hồ sơ
  // cũ thiếu dữ liệu nên giữ optional.
  requireBodyInfo?: boolean
}

export const createCampaignRegisterSchema = (
  t: (key: string) => string,
  { requireBodyInfo = false }: CampaignRegisterSchemaOptions = {}
) => {
  const bodyMetric = (requiredKey: string, minKey: string, min: number) =>
    z
      .string()
      .optional()
      .refine((value) => !requireBodyInfo || !!value?.trim(), {
        message: t(requiredKey),
      })
      .refine(
        (value) =>
          !requireBodyInfo || !value?.trim() || Number(value.trim()) >= min,
        { message: t(minKey) }
      )

  return z.object({
    // '' nghĩa là chưa chọn (Select hiện placeholder); chỉ bắt buộc chọn ở
    // form đăng ký, payload gửi BE chỉ include field này khi đã chọn.
    gender_type: z
      .enum(['MALE', 'FEMALE', ''])
      .refine((value) => !requireBodyInfo || value !== '', {
        message: t('validation.gender_required'),
      }),
    fullName: z
      .string()
      .min(1, { message: t('validation.full_name_required') }),
    phone: z
      .string()
      .min(1, { message: t('validation.phone_required') })
      .regex(/^\d{9,11}$/, { message: t('validation.phone_invalid') }),
    email: z
      .string()
      .min(1, { message: t('validation.email_required') })
      .email({ message: t('validation.email_invalid') }),
    socialLinks: z
      .array(z.string())
      .min(1, { message: t('validation.social_links_required') }),
    sports: z.string().min(1, { message: t('validation.sports_required') }),
    height: bodyMetric(
      'validation.height_required',
      'validation.height_min',
      MIN_HEIGHT_CM
    ),
    weight: bodyMetric(
      'validation.weight_required',
      'validation.weight_min',
      MIN_WEIGHT_KG
    ),
    address: z.string().min(1, { message: t('validation.address_required') }),
    city: z.string().min(1, { message: t('validation.city_required') }),
    district: z.string().min(1, { message: t('validation.district_required') }),
    ward: z.string().min(1, { message: t('validation.ward_required') }),
    agreePolicy: z.boolean().refine((value) => value === true, {
      message: t('validation.agree_policy_required'),
    }),
  })
}

export type CampaignRegisterFormData = z.infer<
  ReturnType<typeof createCampaignRegisterSchema>
>

export const campaignRegisterDefaultValues: CampaignRegisterFormData = {
  gender_type: '',
  fullName: '',
  phone: '',
  email: '',
  socialLinks: [],
  sports: '',
  height: '',
  weight: '',
  address: '',
  city: '',
  district: '',
  ward: '',
  agreePolicy: false,
}
